import { clientLogger } from '@/lib/clientLogger';
import { useState, useCallback, useRef } from 'react';

/**
 * Payment flow states:
 * - idle:       Form is ready for input
 * - processing: confirmPayment() in flight, UI locked
 * - succeeded:  Stripe confirmed client-side (webhook handles DB persistence)
 * - failed:     Payment declined or errored, user can retry
 */
export type PaymentState = 'idle' | 'processing' | 'succeeded' | 'failed';

/** Valid state transitions */
const VALID_TRANSITIONS: Record<PaymentState, PaymentState[]> = {
  idle:       ['processing'],
  processing: ['succeeded', 'failed'],
  succeeded:  [], // terminal — user dismisses via "Back to Job"
  failed:     ['idle', 'processing'],
};

export interface PaymentStateMachine {
  state: PaymentState;
  errorMessage: string | null;
  /** Transition to a new state. Ignores invalid transitions. */
  transitionTo: (next: PaymentState, error?: string) => void;
  /** Reset back to idle (clears error). */
  reset: () => void;
  /** Whether the form should be interactive */
  isFormDisabled: boolean;
  /** Whether we're in a terminal success state */
  isSuccessful: boolean;
}

export function usePaymentStateMachine(): PaymentStateMachine {
  const [state, setState] = useState<PaymentState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const stateRef = useRef<PaymentState>('idle');

  const transitionTo = useCallback((next: PaymentState, error?: string) => {
    const current = stateRef.current;
    const allowed = VALID_TRANSITIONS[current];

    if (!allowed.includes(next)) {
      clientLogger.warn(`[PaymentStateMachine] Invalid transition: ${current} → ${next}`);
      return;
    }

    stateRef.current = next;
    setState(next);

    if (next === 'failed' && error) {
      setErrorMessage(error);
    } else if (next !== 'failed') {
      setErrorMessage(null);
    }
  }, []);

  const reset = useCallback(() => {
    stateRef.current = 'idle';
    setState('idle');
    setErrorMessage(null);
  }, []);

  return {
    state,
    errorMessage,
    transitionTo,
    reset,
    isFormDisabled: state !== 'idle',
    isSuccessful: state === 'succeeded',
  };
}
